/**
 * Human-readable validation report for a production-file batch — one line
 * per roster row plus plain-language explanations of every warning code the
 * pipeline can emit. Most degradations trace back to masterfile authoring
 * (missing panel structure, missing sizes file, size-label mismatches), so
 * the report is written for the designer/ops person fixing the masterfile,
 * not for a developer.
 */

export interface ValidationRowInput {
  label: string;
  status: string;
  /** Raw warning codes collected for the row (may repeat across rows). */
  warnings: string[];
  /** Hard error message, when the row failed outright. */
  error?: string | null;
}

export interface ValidationReportInput {
  orderNo: string | number | null | undefined;
  productName: string;
  mode: 'all' | 'nested';
  /** Run-level notes printed under the header (e.g. panels the worker omitted). */
  notes?: string[];
  rows: ValidationRowInput[];
}

const EXPLANATIONS: Array<[RegExp, string]> = [
  [
    /^ungraded-base-size$/,
    'No sizes (grading) file on this product — exported UNGRADED at base size.',
  ],
  [
    /^base-size-not-found$/,
    'The sizes file has no base-size group (L) — grading ratios could not be computed.',
  ],
  [
    /^base-size-inferred:/,
    'The sizes file has no Anchor marking the base size, so the base was taken from the size the production atlas is drawn at (its wrapper group, or L), matched within the roster size’s family (e.g. Men 4XL grades off Men_L). The resolved id is shown in the code.',
  ],
  [
    /^base-size-assumed-first-group:/,
    'The sizes file has no Anchor and no group matching the atlas base size — the FIRST size group in the file was used as the grading base. Grading is probably wrong; add an Anchor to the base size group or name the groups to match.',
  ],
  [
    /^size-id-matched-loosely:/,
    'The roster size label did not equal the sizes-SVG group id verbatim and was matched by normalizing it (an SVG id cannot contain a space, so design tools rewrite "Men XL" as e.g. Men_x20_XL). The resolved id is shown in the code; verify it is the intended size.',
  ],
  [
    /^production-design-wrapper-unwrapped:/,
    'The production masterfile wraps the whole atlas in one group (named in the code, e.g. the size L). The wrapper was unwrapped so the Anchor and the panels could be read; no action needed unless placements look off.',
  ],
  [
    /^clippath-id-deduped:/,
    'The design reuses one clipPath id for several clip shapes (the design editor writes every trimmed stripe as "<name>__trim"). Each reference was re-pointed to the definition beside it, as the author intended; browsers would have used the first one for all of them.',
  ],
  [
    /^nested-clippath-flattened:/,
    'A clipped shape sat inside a clipped group (a trimmed stripe inside a sleeve clip). The two clips were merged into their geometric intersection so the file can be written — no visual change.',
  ],
  [
    /^nested-clippath-empty:/,
    'A clipped shape sat inside a clipped group and the two clips do not overlap — nothing of that shape is visible (the builder shows nothing either), so it was left out of the file.',
  ],
  [
    /^nested-clippath-dropped:/,
    'A clipped shape sat inside a clipped group and the clips could not be merged — the outer clip was dropped, so that shape may bleed past its panel. Check the piece.',
  ],
  [
    /^loose-artwork-assigned:/,
    'Artwork was found outside the atlas group of the production masterfile (drawn straight into the exported template) and was filed under the panel it lies on — the code shows element → panel. It grades, isolates and PowerClips with that panel. Move it into the panel group in the masterfile to silence this.',
  ],
  [
    /^loose-artwork-unassigned:/,
    'Artwork outside the atlas group lies on no panel — it is NOT part of any production piece. Check the masterfile for stray shapes.',
  ],
  [
    /^text-fit-condensed:/,
    'A name or number ran past its panel’s cut edge. It was squeezed sideways — height unchanged — to sit 2.5 cm inside both edges (code: type, text, panel, width kept).',
  ],
  [
    /^text-fit-moved:/,
    'A name or number crossed its panel’s cut edge but fits the panel width. It was moved inward to sit 2.5 cm inside the edge; its size is unchanged.',
  ],
  [
    /^text-fit-no-room:/,
    'A name or number crosses its panel’s cut edge, but the panel is narrower than the two 2.5 cm margins there — it was left as placed. Check the piece.',
  ],
  [
    /^text-font-missing:/,
    'A text’s font has no file on the order (code: font, text) — the text is NOT in this file. For an uploaded font (customfont-<id>) the order API could not find it for this order’s customer/company.',
  ],
  [
    /^text-font-not-found:/,
    'A text’s font file could not be downloaded from storage (code: font, text) — the text is NOT in this file.',
  ],
  [
    /^text-font-unreadable:/,
    'A text’s font file is not a font the generator can read (code: font, text) — the text is NOT in this file. TTF, OTF, WOFF and WOFF2 are supported.',
  ],
  [
    /^panel-namespace-missing$/,
    'The masterfile has no *Panel* clipPath structure — panels were detected from top-level groups instead. Verify the piece count matches the physical garment.',
  ],
  [
    /^panel-split-islands:/,
    'One panel id in the masterfile covers several separate pieces of fabric (the code shows panel and count). Each piece was nested and labelled on its own ("innerpanels 2"). Give every physical panel its own clipPath in the masterfile to silence this.',
  ],
  [
    /^panel-omit-not-applied$/,
    'Panels were unticked in setup, but a panel of this row could not be isolated from the artwork, so the file keeps the whole composite — the omitted panels are still in it. Check the masterfile panel structure.',
  ],
  [
    /^panel-isolation-fallback$/,
    'A panel could not be isolated from the artwork — its piece is a windowed rectangle that may include neighbor artwork, and it gets no laser outline.',
  ],
  [
    /^laser-outline-missing$/,
    'The cut silhouette could not be computed for a panel — that piece is packed without laser-cutting outlines.',
  ],
  [
    /^corel-clips-remaining:(\d+)$/,
    'Some clip masks could not be flattened to plain geometry — the file may render incompletely in CorelDRAW (count in the code).',
  ],
  [
    /^logo-image-count-mismatch/,
    'The number of raster logos in the artwork did not match the stored logo list — some logos may not have been vectorized.',
  ],
  [/^logo-vectorize-/, 'A logo could not be vectorized and stays as an embedded raster image.'],
  [
    /^logo-kept-original-raster:/,
    'Staff chose the uploaded file for this logo in the Logo inspector and it is a raster — it stays an embedded image in the production file, as approved.',
  ],
  [
    /^logo-original-vector-not-svg:/,
    'Staff chose the uploaded file for this logo, but it is a PDF/EPS/AI that cannot be placed inside the SVG — the raster the builder placed is kept.',
  ],
  [
    /^logo-manual-vector-not-svg:/,
    "The designer's manual vector for this logo is a PDF/EPS/AI and cannot be placed inside the SVG — the auto trace was used instead. Upload the manual version as SVG to have it placed.",
  ],
  [
    /^logo-manual-vector-missing:/,
    'The order says to use the manual vector for this logo, but none exists beside it any more — the auto trace was used instead.',
  ],
];

function explain(code: string): string | null {
  for (const [re, text] of EXPLANATIONS) {
    if (re.test(code)) return text;
  }
  return null;
}

export function buildValidationReport(input: ValidationReportInput): string {
  const lines: string[] = [
    `Validation report — order ${input.orderNo ?? 'n/a'} — ${input.productName}`,
    `Mode: ${input.mode === 'nested' ? 'nested sheets' : 'per-row production files'}`,
    ...(input.notes ?? []),
    '',
  ];

  const counts = { ok: 0, warning: 0, error: 0, skipped: 0 };
  const seenCodes = new Set<string>();

  for (const row of input.rows) {
    const status = row.status === 'done' ? 'ok' : row.status;
    if (status === 'ok') counts.ok++;
    else if (status === 'warning') counts.warning++;
    else if (status === 'error') counts.error++;
    else if (status === 'skipped') counts.skipped++;

    let line = `[${status.toUpperCase().padEnd(7)}] ${row.label}`;
    if (row.error) line += ` — ${row.error}`;
    else if (row.warnings.length) line += ` — ${[...new Set(row.warnings)].join(', ')}`;
    lines.push(line);
    row.warnings.forEach((w) => seenCodes.add(w));
  }

  lines.push(
    '',
    `Rows: ${input.rows.length} total — ${counts.ok} ok, ${counts.warning} with warnings, ` +
      `${counts.error} failed, ${counts.skipped} skipped`
  );

  const explained = [...seenCodes]
    .map((code) => ({ code, text: explain(code) }))
    .filter((e): e is { code: string; text: string } => e.text !== null);
  if (explained.length) {
    lines.push('', 'What the warnings mean:');
    const dedup = new Map<string, string>();
    for (const e of explained) if (!dedup.has(e.text)) dedup.set(e.text, e.code);
    for (const [text, code] of dedup) lines.push(`- ${code}: ${text}`);
  }

  return lines.join('\n') + '\n';
}
